import { Link } from 'react-router-dom'
import type { Job } from '../api/types'
import { jobBanners } from '../lib/aiHealth'

/** Prominent, admin-visible failure states the AI worker produces. These are
 *  buried nowhere: a job's `error` field carries them, but a table row is not
 *  where an admin looks first, so they surface here instead. */
export function AiHealthBanners({ jobs }: { jobs: Job[] }) {
  const { invalidKey, budget } = jobBanners(jobs)
  if (invalidKey.length === 0 && budget.length === 0) return null

  return (
    <div className="space-y-2">
      {invalidKey.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-xs text-rose-900"
        >
          <p className="font-semibold">
            <span aria-hidden="true">⚠</span> AI provider key rejected
          </p>
          <p className="mt-0.5">
            {invalidKey.length} job{invalidKey.length === 1 ? '' : 's'} failed because the stored
            provider key was refused or could not be decrypted. No further calls are made with it.
          </p>
          <p className="mt-1">
            <strong>Remedy:</strong>{' '}
            <Link to="/settings/ai-providers" className="underline hover:no-underline">
              Open AI providers
            </Link>{' '}
            and re-enter a valid key, or disable the provider until it is fixed.
          </p>
        </div>
      )}
      {budget.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"
        >
          <p className="font-semibold">
            <span aria-hidden="true">⚠</span> Daily AI budget reached
          </p>
          <p className="mt-0.5">
            {budget.length} job{budget.length === 1 ? '' : 's'} paused — today's request/character
            budget (Europe/Berlin calendar day) is exhausted, so the provider is not being called.
          </p>
          <p className="mt-1">
            <strong>Remedy:</strong> wait for the Berlin day to roll over, or raise the daily budget
            on the server. Jobs resume automatically once budget is available — no action needed
            beyond that.
          </p>
        </div>
      )}
    </div>
  )
}
